import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {useDebounce} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CellsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {ItemIndexes} from '../../../srcCommon/helpers/itemIndexHelpers';
import {ResizeSeparator} from '../../components/common/resizeSeparator';
import {
  CellsItems,
  CellsItemsRequestMoreAsyncHandler,
} from '../../components/inboxes/cells/content/cellsItemsBase';
import {AuditTrailActivity} from '../../models/api/auditTrailActivityModel';
import {LocalPreferencesSizes, LocalPreferencesSizesEnum} from '../../models/api/localPreferencesModel';
import {ReselectDirectionsEnum} from '../../models/api/preferencesModel';
import {SizeUpdater} from '../../store/data/localPreferences/localPreferencesActions';
import {CellsPanelAuditTrailPageState} from '../../store/panels/cells/cellsPanelState';
import {InboxesAuditTrailParameters} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsNormalSelection,
  InboxesSelection,
} from '../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {AuditTrailCellContainer} from './auditTrailCellContainer';

interface AuditTrailActivityCellsProps {
  panelId: CellsPanelId;
  userId: number;
  sortedActivities: ReadonlyArray<AuditTrailActivity>;
  sortedActivityIds: ReadonlyArray<number>;
  activityIndexes: ItemIndexes;
  page: CellsPanelAuditTrailPageState;
  onParametersChange: (parameters: InboxesAuditTrailParameters) => void;
  onRetryClick: () => void;
  onRequestMoreAsync: CellsItemsRequestMoreAsyncHandler;
  sizes: LocalPreferencesSizes;
  updateSizes: SizeUpdater;
}

/*
 * Styles.
 */

const StyledActivityCellsDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  grid-column: 1;
  grid-row: 3;
  display: flex;
`;

const StyledRegionGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto auto 1fr;
`;

/*
 * Component.
 */

const minSizePanelWidth = 100;
const maxSizePanelWidth = 400;
const cellItemHeight = 63;

export const AuditTrailCells: FC<AuditTrailActivityCellsProps> = (props) => {
  const {panelId, page, userId, sizes, onParametersChange, onRequestMoreAsync, onRetryClick} = props;
  const {parameters} = page;
  const {selection: currentSelection} = parameters;
  const buildSelectionParameters = useCallback(
    (selection: InboxesSelection<number>): InboxesAuditTrailParameters => ({
      ...parameters,
      selection,
    }),
    [parameters],
  );

  const onSelectionChange: (arg: InboxesSelection<number>) => void = useCallback(
    (selection) => onParametersChange(buildSelectionParameters(selection)),
    [buildSelectionParameters, onParametersChange],
  );

  const computedIsSelected = useCallback(
    (activityId: number) =>
      Boolean(
        computeIsNormalSelection(currentSelection) && currentSelection.selectedIds.includes(activityId),
      ),
    [currentSelection],
  );
  const theme = useUserTheme();
  const [sideWidth, setSideWidth] = useState(sizes[LocalPreferencesSizesEnum.AUDIT_CELLS]);

  const onSideWidthChange: (arg: number) => void = (newWidth) => {
    const boundedSideWidth = Math.min(maxSizePanelWidth, Math.max(minSizePanelWidth, newWidth));
    setSideWidth(boundedSideWidth);
    saveSideWidthDebounced(boundedSideWidth);
  };

  const saveSideWidthDebounced = useDebounce((newWidth: number) => {
    props.updateSizes(LocalPreferencesSizesEnum.AUDIT_CELLS, newWidth);
  }, 1000);

  return (
    // need to return a fragment for grid positioning by the parent.
    <>
      <StyledActivityCellsDiv style={{width: sideWidth}}>
        <StyledRegionGrid>
          <CellsItems
            userId={userId}
            panelId={panelId}
            hasNextPage={false}
            page={page}
            reselectDirection={ReselectDirectionsEnum.SMART}
            itemIds={props.sortedActivityIds}
            itemIndexes={props.activityIndexes}
            renderItem={(id, onClick) => {
              const index = props.activityIndexes[id];
              const activity = props.sortedActivities[index];

              return (
                <AuditTrailCellContainer
                  key={id}
                  activityDetail={activity}
                  isSelected={computedIsSelected(id)}
                  onClick={onClick}
                />
              );
            }}
            defaultId={0}
            focusedId={undefined}
            onSelectionChange={onSelectionChange}
            onRetryClick={onRetryClick}
            onRequestMoreAsync={onRequestMoreAsync}
            isCompact={false}
            isOffline={false}
            cellHeight={cellItemHeight}
          >
            {(cells) => cells}
          </CellsItems>
        </StyledRegionGrid>
      </StyledActivityCellsDiv>
      <ResizeSeparator
        column={2}
        row={3}
        size={sideWidth}
        minSize={minSizePanelWidth}
        maxSize={maxSizePanelWidth}
        onSizeChange={onSideWidthChange}
        color={theme.leftBar.separator}
      />
    </>
  );
};
